// M149 — Publieke voorwaarden-pagina. Verplicht voor Google-verification.
import { Link, useSearchParams } from "react-router-dom";

const NL = () => (
  <>
    <h1 className="text-3xl md:text-4xl font-bold text-text-primary tracking-tight mb-2">Algemene Voorwaarden</h1>
    <p className="text-sm text-text-muted mb-8">Laatst bijgewerkt: 17 september 2026</p>
    <section className="prose-section">
      <h2>Welke dienst leveren we?</h2>
      <p>JustEasySEO is een AI-marketingassistent die je zakelijk advies geeft over de vindbaarheid, presentatie en advertenties van je website. We wijzigen zelf niets. Alle voorstellen pas je zelf toe of laat je toepassen door een derde partij. We garanderen geen ranking-, verkeer- of omzet-resultaat: onze adviezen zijn gebaseerd op de gegevens die jij aanlevert plus openbare informatie, en zoekmachine-uitkomsten zijn nooit voorspelbaar.</p>

      <h2>Wat verwachten we van jou?</h2>
      <ul>
        <li>Correcte bedrijfsinformatie in je profiel.</li>
        <li>Eigen verantwoordelijkheid voor het toepassen van adviezen (of het niet toepassen).</li>
        <li>Correct gebruik van de dienst — geen misbruik, geen poging tot inbreuk of hergebruik voor derden.</li>
        <li>Naleving van de voorwaarden van gekoppelde platforms (Google, sociale netwerken).</li>
      </ul>

      <h2>Aansprakelijkheid</h2>
      <p>Onze aansprakelijkheid is beperkt tot herstel of vervanging van de dienst zelf. We zijn niet aansprakelijk voor gederfde omzet, gevolgschade of schade aan derden. Deze beperking geldt niet bij opzet of grove nalatigheid van onze kant.</p>

      <h2>Prijzen en beëindiging</h2>
      <p>Tarieven staan op de website of in je offerte. Je kunt je abonnement op elk moment beëindigen; opzegging gaat in aan het einde van de lopende factuurperiode. Restitutie voor niet-gebruikte perioden vindt plaats bij ernstige tekortkomingen van onze kant.</p>

      <h2>Wijzigingen van deze voorwaarden</h2>
      <p>We kunnen deze voorwaarden aanpassen. Belangrijke wijzigingen kondigen we ten minste 30 dagen van tevoren aan via e-mail of in de app. Wil je niet akkoord? Dan kun je je account beëindigen voordat de nieuwe voorwaarden ingaan.</p>

      <h2>Toepasselijk recht</h2>
      <p>Op deze voorwaarden is Nederlands recht van toepassing. Geschillen worden bij voorkeur eerst besproken en vervolgens voorgelegd aan de bevoegde rechter in Nederland.</p>

      <h2>Contact</h2>
      <p>Vragen? Mail <a href="mailto:info@justeasyseo.com">info@justeasyseo.com</a>.</p>
    </section>
  </>
);

const EN = () => (
  <>
    <h1 className="text-3xl md:text-4xl font-bold text-text-primary tracking-tight mb-2">Terms of Service</h1>
    <p className="text-sm text-text-muted mb-8">Last updated: 17 September 2026</p>
    <section className="prose-section">
      <h2>What service we provide</h2>
      <p>JustEasySEO is an AI marketing assistant that provides business advice about the visibility, presentation and advertising of your website. We do not make changes ourselves. You apply, or arrange for a third party to apply, all proposals yourself. We do not guarantee any ranking, traffic or revenue outcome: our advice is based on data you provide plus public information, and search-engine results are never predictable.</p>

      <h2>What we expect from you</h2>
      <ul>
        <li>Accurate business information in your profile.</li>
        <li>Own responsibility for applying (or not applying) advice.</li>
        <li>Fair use of the service — no abuse, no attempted breach, no re-use for third parties.</li>
        <li>Compliance with the terms of connected platforms (Google, social networks).</li>
      </ul>

      <h2>Liability</h2>
      <p>Our liability is limited to repair or replacement of the service itself. We are not liable for lost revenue, consequential damage or damage to third parties. This limitation does not apply in cases of intent or gross negligence on our part.</p>

      <h2>Pricing and termination</h2>
      <p>Rates are published on the website or in your quote. You can terminate your subscription at any time; termination takes effect at the end of the current billing period. Refunds for unused periods apply in the event of a serious failure on our part.</p>

      <h2>Changes to these terms</h2>
      <p>We may amend these terms. We announce important changes at least 30 days in advance by email or in the app. If you do not agree? You can terminate your account before the new terms take effect.</p>

      <h2>Governing law</h2>
      <p>Dutch law applies to these terms. Disputes are preferably discussed first and then submitted to the competent court in the Netherlands.</p>

      <h2>Contact</h2>
      <p>Questions? Email <a href="mailto:info@justeasyseo.com">info@justeasyseo.com</a>.</p>
    </section>
  </>
);

export default function VoorwaardenPage() {
  const [sp] = useSearchParams();
  const lang = sp.get("lang") === "en" ? "en" : "nl";
  const otherLang = lang === "en" ? "" : "?lang=en";
  return (
    <div className="min-h-screen bg-bg-canvas text-text-primary">
      <header className="max-w-[900px] mx-auto px-6 sm:px-12 py-6 flex items-center justify-between">
        <Link to="/home" className="text-xl font-bold tracking-tight text-accent-teal">
          Just<span className="text-text-primary">Easy</span>SEO
        </Link>
        <Link to={`/voorwaarden${otherLang}`} className="text-sm text-text-muted hover:text-text-primary">
          {lang === "en" ? "Nederlands" : "English"}
        </Link>
      </header>
      <main id="main-content" className="max-w-[820px] mx-auto px-6 sm:px-12 pb-16">
        <style>{`
          .prose-section h2 { font-size: 1.35rem; font-weight: 700; color: #172C32; margin-top: 2rem; margin-bottom: 0.5rem; }
          .prose-section p, .prose-section li { font-size: 0.95rem; color: #172C32; line-height: 1.65; }
          .prose-section ul { list-style: disc; padding-left: 1.4rem; margin: 0.5rem 0 1rem 0; }
          .prose-section a { color: #066A75; text-decoration: underline; }
        `}</style>
        {lang === "en" ? <EN /> : <NL />}
        <div className="mt-12 text-sm">
          <Link to="/home" className="text-accent-teal-dim hover:text-accent-teal">← {lang === "en" ? "Back to home" : "Terug naar home"}</Link>
        </div>
      </main>
    </div>
  );
}
